import { Modal, Radio, Form, Button, Space } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormattedInputNumber } from '../../../components/Misc/FormattedInputNumber'
import { useResetFormOnCloseModal } from '../../../components/Form/hooks'
import { EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'
import { DeprecationMethodId } from '../types'
import { useCreateDeprecationPlan } from '../hooks/useCreateDeprecationPlan'
import { useUpdateDeprecationPlan } from '../hooks/useUpdateDeprecationPlan'
import { useDeleteDeprecationPlan } from '../hooks/useDeleteDeprecationPlan'
import { useDeprecationPlanContext } from '../context/DeprecationPlanContext'

interface DeprecationModalProps {
  visible: boolean
  rowData: EnhancedStatementRow
  onClose: () => void
}

export const DeprecationModal: React.FC<DeprecationModalProps> = ({ visible, rowData, onClose }) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const { getDeprecationPlanForAccount } = useDeprecationPlanContext()

  const createMutation = useCreateDeprecationPlan()
  const updateMutation = useUpdateDeprecationPlan()
  const deleteMutation = useDeleteDeprecationPlan()

  const deprecationMethodId = Form.useWatch('deprecationMethodId', form)

  // Lookup deprecation plan from context
  const deprecationPlan = useMemo(() => {
    if (!rowData) return undefined
    return getDeprecationPlanForAccount({
      balanceSheetRowId: rowData._sourceStatementRowId
    })
  }, [rowData, getDeprecationPlanForAccount])

  useEffect(() => {
    if (visible && rowData?._sourceStatementRowId) {
      form.setFieldsValue({
        deprecationMethodId: deprecationPlan?.deprecationMethodId || DeprecationMethodId.StraightLineDeprecation,
        value: deprecationPlan?.value,
        balanceSheetRowId: rowData._sourceStatementRowId
      })
    }
  }, [visible, rowData, deprecationPlan, form])

  useResetFormOnCloseModal({ form, open: visible })

  const radioStyle = {
    display: 'block',
    height: '30px',
    lineHeight: '30px'
  }

  const handleCancel = () => {
    onClose()
    form.resetFields()
  }

  const handleDeprecationMethodChange = (id: number) => {
    const value = id === DeprecationMethodId.ReducingBalanceMethod ? 0.01 : 1
    form.setFieldsValue({ value })
  }

  const handleDelete = async () => {
    if (!deprecationPlan?.id) return

    Modal.confirm({
      title: t('modal:deleteTitle', 'Haluatko varmasti poistaa?'),
      content: t('financialStatementsPage:deprecation-delete-confirm', 'Poissonsuunnitelma poistetaan'),
      okText: t('global:delete'),
      okType: 'danger',
      cancelText: t('global:cancel'),
      onOk: async () => {
        await deleteMutation.mutateAsync(deprecationPlan.id)
        handleCancel()
      }
    })
  }

  const handleOk = async () => {
    try {
      const formData = await form.validateFields()
      const payload = {
        balanceSheetRowId: formData.balanceSheetRowId,
        deprecationMethodId: formData.deprecationMethodId,
        value: formData.value
      }

      if (deprecationPlan?.id) {
        // Update existing deprecation plan
        await updateMutation.mutateAsync({ id: deprecationPlan.id, data: payload })
        handleCancel()
      } else {
        // Create new deprecation plan
        await createMutation.mutateAsync(payload)
        handleCancel()
      }
    } catch (error) {
      // Form validation error or mutation error - already handled by hooks
      console.error('Failed to submit deprecation plan:', error)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending
  const hasExistingPlan = !!deprecationPlan

  return (
    <Modal
      width={350}
      open={visible}
      title={t('financialStatementsPage:deprecation-add')}
      maskClosable={false}
      onCancel={handleCancel}
      destroyOnHidden
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {hasExistingPlan && (
              <Button danger onClick={handleDelete}>
                {t('global:delete')}
              </Button>
            )}
          </div>
          <Space>
            <Button onClick={handleCancel}>{t('global:cancel')}</Button>
            <Button type="primary" onClick={handleOk} loading={isLoading}>
              {t('global:save')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form layout="vertical" form={form}>
        <Form.Item name="balanceSheetRowId" hidden>
          <FormattedInputNumber />
        </Form.Item>

        <Form.Item name="deprecationMethodId" label={t('financialStatementsPage:deprecation-method')}>
          <Radio.Group onChange={e => handleDeprecationMethodChange(e.target.value)}>
            <Radio style={radioStyle} value={DeprecationMethodId.StraightLineDeprecation}>
              {t('financialStatementsPage:straight-line-depreciation')}
            </Radio>
            <Radio style={radioStyle} value={DeprecationMethodId.ReducingBalanceMethod}>
              {t('financialStatementsPage:reducing-balance-method')}
            </Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const config =
              deprecationMethodId === DeprecationMethodId.StraightLineDeprecation
                ? {
                    label: t('financialStatementsPage:years'),
                    inputProps: { min: 1, max: 40 }
                  }
                : {
                    label: t('financialStatementsPage:percent'),
                    inputProps: { percentage: true, min: 1, max: 25, step: 1, addonAfter: '%' }
                  }

            return (
              <Form.Item
                name="value"
                label={config.label}
                rules={[{ required: true, message: t('global:required-field') }]}
              >
                <FormattedInputNumber
                  {...config.inputProps}
                  onClick={event => event.currentTarget.select()}
                  style={{
                    width: 100
                  }}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default DeprecationModal
